import React, { useEffect, useRef, useState } from "react";
import InfiniteScroll from "react-infinite-scroll-component";
import ApiService from "../../api/apiService.js";
import showSwal from "../swal.jsx";
import { common } from "../../util/common.js";
import RequestHistoryItem from "./reusable/request-history-item.jsx";
import FormsSearch from "./reusable/forms-search.jsx";
import "./request-main.css";

function RequestsHistory() {
  const PAGE_SIZE = 8;

  const [formData, setFormData] = useState([]);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [isFetchingMore, setIsFetchingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [searchKeyword, setSearchKeyword] = useState("");
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: PAGE_SIZE,
  });

  const debounceRef = useRef(null);

  const getForms = async ({
    page = 1,
    pageSize = PAGE_SIZE,
    keyword = "",
    append = false,
  } = {}) => {
    try {
      if (append) {
        setIsFetchingMore(true);
      } else {
        setIsInitialLoading(true);
      }

      const response = await ApiService.forms.getRequestsHistoryV2(
        page,
        pageSize,
        keyword
      );

      const { success, requests = [] } = response || {};

      if (!success) {
        showSwal(common.errors.GETTING_APPROVAL_LIST_ERROR);
        setHasMore(false);
        return;
      }

      setFormData((prev) => {
        if (!append) return requests;

        const existingIds = new Set(prev.map((item) => item?.id));
        const dedupedNewItems = requests.filter(
          (item) => !existingIds.has(item?.id)
        );

        return [...prev, ...dedupedNewItems];
      });

      setHasMore(requests.length === pageSize);
      setPagination((prev) => ({
        ...prev,
        page,
        pageSize,
      }));
    } catch (error) {
      console.error("Failed to fetch request history:", error);
      showSwal(common.errors.GETTING_APPROVAL_LIST_ERROR);
      setHasMore(false);
    } finally {
      setIsInitialLoading(false);
      setIsFetchingMore(false);
    }
  };

  useEffect(() => {
    getForms({
      page: 1,
      pageSize: PAGE_SIZE,
      keyword: "",
      append: false,
    });
  }, []);

  const fetchMoreData = async () => {
    if (isFetchingMore || !hasMore || isInitialLoading) return;

    await getForms({
      page: pagination.page + 1,
      pageSize: pagination.pageSize,
      keyword: searchKeyword,
      append: true,
    });
  };

  const handleSearch = (query) => {
    const trimmedQuery = query.trim();

    setSearchKeyword(trimmedQuery);
    setHasMore(true);

    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(() => {
      setFormData([]);
      getForms({
        page: 1,
        pageSize: PAGE_SIZE,
        keyword: trimmedQuery,
        append: false,
      });
    }, 400);
  };

  return (
    <div className="request-main">
      <div className="request-main-title">
        <div className="request-main-title__top">
          <h3>Tagging History</h3>
        </div>

        <div className="request-main-title__search">
          <FormsSearch handleSearch={handleSearch} />
        </div>
      </div>

      {isInitialLoading ? (
        <div className="loader--container">
          <div className="loader--"></div>
        </div>
      ) : (
        <div
          id="requests-history-scrollable-container"
          className="request-main---container"
        >
          <InfiniteScroll
            dataLength={formData.length}
            next={fetchMoreData}
            hasMore={hasMore}
            loader={
              <div className="request-main__bottom-loader">
                <div className="loader-- small"></div>
                <span>Loading more history...</span>
              </div>
            }
            endMessage={
              formData.length > 0 ? (
                <p className="request-main__end-message">
                  You’ve reached the end.
                </p>
              ) : null
            }
            scrollableTarget="requests-history-scrollable-container"
            scrollThreshold="180px"
          >
            {formData.length > 0 ? (
              <div className="request-main__list">
                {formData.map((data) => (
                  <div className="request-main__item" key={data?.id}>
                    <RequestHistoryItem data={data} />
                  </div>
                ))}
              </div>
            ) : (
              <div className="request-main__empty-state">
                <div className="request-main__empty-icon">📜</div>
                <h4>No request history found</h4>
                <p>
                  {searchKeyword
                    ? "Try changing your search keyword."
                    : "There is no request history available right now."}
                </p>
              </div>
            )}
          </InfiniteScroll>
        </div>
      )}
    </div>
  );
}

export default RequestsHistory;